// Direct-to-Cloudinary upload helper with support for:
// 1. Unsigned direct image uploads (for avatars, banners, and thumbnails).
// 2. Unsigned chunked video uploads with live progress callbacks (X-Unique-Upload-Id & Content-Range).
//
// Cloudinary Free Plan Limits Enforced:
// - Maximum Image Size: 10 MB (10,485,760 bytes)
// - Maximum Video Size: 100 MB (104,857,600 bytes)
//
// Why chunked client-side upload?
// - Free hosting tiers (e.g. Render) have strict request body size and 10s request timeout limits.
// - Slicing large videos into 6MB chunks directly to Cloudinary prevents timeout, socket drops, and server load.
// - Once Cloudinary processes the video, the client sends a tiny JSON payload (~1KB) to the backend which completes in <50ms.

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB Cloudinary Free Plan limit
export const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB Cloudinary Free Plan limit

const DEFAULT_CLOUD_NAME = process.env.CLOUDINARY_CLOUD_NAME || "ddxq9mouk";
const DEFAULT_UPLOAD_PRESET = process.env.CLOUDINARY_UPLOAD_PRESET || "wideo-cloudinary-app-preset";
const DEFAULT_VIDEO_PRESET = process.env.CLOUDINARY_VIDEO_UPLOAD_PRESET || DEFAULT_UPLOAD_PRESET;

/**
 * Validates image size against Cloudinary Free Plan 10MB limit.
 */
function validateImageSize(file) {
    if (file && file.size > MAX_IMAGE_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        throw new Error(
            `Image size (${sizeMB} MB) exceeds Cloudinary's 10 MB free plan limit.`
        );
    }
}

/**
 * Validates video size against Cloudinary Free Plan 100MB limit.
 */
function validateVideoSize(file) {
    if (file && file.size > MAX_VIDEO_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        throw new Error(
            `Video size (${sizeMB} MB) exceeds Cloudinary's 100 MB free plan limit.`
        );
    }
}

/**
 * Uploads an image (thumbnail, profile avatar, banner) directly to Cloudinary.
 * @param {File|Blob} file - The image file to upload.
 * @returns {Promise<string>} - Resolves with the HTTPS secure URL of the uploaded image.
 */
export async function uploadImageToCloudinary(file) {
    validateImageSize(file);

    const cloudName = process.env.CLOUDINARY_CLOUD_NAME || DEFAULT_CLOUD_NAME;
    const uploadPreset = process.env.CLOUDINARY_UPLOAD_PRESET || DEFAULT_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
        throw new Error(
            "Cloudinary isn't configured. Set CLOUDINARY_CLOUD_NAME and CLOUDINARY_UPLOAD_PRESET in client/.env."
        );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);

    const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: "POST", body: formData }
    );

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error?.message || "Image upload failed");
    }

    const data = await response.json();
    return data.secure_url;
}

/**
 * Uploads an image and returns full metadata (secure_url and public_id).
 */
export async function uploadImageDetailsToCloudinary(file) {
    validateImageSize(file);

    const cloudName = process.env.CLOUDINARY_CLOUD_NAME || DEFAULT_CLOUD_NAME;
    const uploadPreset = process.env.CLOUDINARY_UPLOAD_PRESET || DEFAULT_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
        throw new Error(
            "Cloudinary isn't configured. Set CLOUDINARY_CLOUD_NAME and CLOUDINARY_UPLOAD_PRESET in client/.env."
        );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);

    const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: "POST", body: formData }
    );

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error?.message || "Image upload failed");
    }

    const data = await response.json();
    return {
        secure_url: data.secure_url,
        public_id: data.public_id || "",
    };
}

/**
 * Uploads a video file directly to Cloudinary using Request Chunking (HTTP Content-Range).
 * Slices large videos into 6MB binary chunks to ensure fast transmission and zero timeout issues.
 * @param {File|Blob} file - The video file to upload.
 * @param {Object} options - Configuration and progress options.
 * @param {Function} [options.onProgress] - Callback receiving `{ percent, currentChunk, totalChunks, bytesUploaded, totalBytes, status }`.
 * @param {number} [options.chunkSize] - Size of each chunk in bytes (default 6MB, min 5MB for Cloudinary).
 * @returns {Promise<{ secure_url: string, videoUrl: string, public_id: string, duration: number, format: string, bytes: number }>}
 */
export async function uploadVideoInChunksToCloudinary(file, options = {}) {
    validateVideoSize(file);

    const {
        onProgress = () => {},
        chunkSize = 6 * 1024 * 1024, // 6MB chunk size (Cloudinary requires minimum 5MB per chunk)
    } = options;

    const cloudName = process.env.CLOUDINARY_CLOUD_NAME || DEFAULT_CLOUD_NAME;
    const uploadPreset = process.env.CLOUDINARY_VIDEO_UPLOAD_PRESET || process.env.CLOUDINARY_UPLOAD_PRESET || DEFAULT_VIDEO_PRESET;

    if (!cloudName || !uploadPreset) {
        throw new Error(
            "Cloudinary isn't configured. Set CLOUDINARY_CLOUD_NAME and CLOUDINARY_VIDEO_UPLOAD_PRESET in client/.env."
        );
    }

    const fileSize = file.size;

    // Direct single upload if the video is smaller than or equal to one chunk size
    if (fileSize <= chunkSize) {
        onProgress({
            percent: 20,
            currentChunk: 1,
            totalChunks: 1,
            bytesUploaded: 0,
            totalBytes: fileSize,
            status: "Uploading video directly...",
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);
        formData.append("resource_type", "video");

        const response = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody?.error?.message || "Video upload failed");
        }

        const data = await response.json();

        onProgress({
            percent: 100,
            currentChunk: 1,
            totalChunks: 1,
            bytesUploaded: fileSize,
            totalBytes: fileSize,
            status: "Video upload complete!",
        });

        return {
            secure_url: data.secure_url,
            videoUrl: data.secure_url,
            public_id: data.public_id || "",
            duration: Math.round(data.duration || 0),
            format: data.format || "mp4",
            bytes: data.bytes || fileSize,
        };
    }

    // Chunked multipart upload for larger video files
    const totalChunks = Math.ceil(fileSize / chunkSize);
    const uniqueUploadId = `wideo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    let finalData = null;
    let bytesUploaded = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize - 1, fileSize - 1);
        const chunkBlob = file.slice(start, end + 1);

        const formData = new FormData();
        formData.append("file", chunkBlob);
        formData.append("upload_preset", uploadPreset);
        formData.append("cloud_name", cloudName);

        const contentRange = `bytes ${start}-${end}/${fileSize}`;

        const progressPercent = Math.round((chunkIndex / totalChunks) * 95);
        onProgress({
            percent: progressPercent,
            currentChunk: chunkIndex + 1,
            totalChunks,
            bytesUploaded,
            totalBytes: fileSize,
            status: `Uploading chunk ${chunkIndex + 1} of ${totalChunks}...`,
        });

        const response = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
            {
                method: "POST",
                headers: {
                    "X-Unique-Upload-Id": uniqueUploadId,
                    "Content-Range": contentRange,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(
                errorBody?.error?.message ||
                `Failed uploading chunk ${chunkIndex + 1} of ${totalChunks}`
            );
        }

        bytesUploaded += (end - start + 1);
        const responseData = await response.json().catch(() => ({}));

        // The final chunk returns the completed Cloudinary video asset details
        if (chunkIndex === totalChunks - 1) {
            finalData = responseData;
        }
    }

    onProgress({
        percent: 100,
        currentChunk: totalChunks,
        totalChunks,
        bytesUploaded: fileSize,
        totalBytes: fileSize,
        status: "Upload complete! Finalizing video...",
    });

    if (!finalData || !finalData.secure_url) {
        throw new Error("Video upload completed but Cloudinary did not return a valid video URL.");
    }

    return {
        secure_url: finalData.secure_url,
        videoUrl: finalData.secure_url,
        public_id: finalData.public_id || "",
        duration: Math.round(finalData.duration || 0),
        format: finalData.format || "mp4",
        bytes: finalData.bytes || fileSize,
    };
}
